import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'

export const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/auth/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/auth/RegisterView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/auth/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/auth/ResetPasswordView.vue'),
    meta: { public: true },
  },
  // Public fallback views (CLI-016)
  {
    path: '/catalogo',
    name: 'public-catalog',
    component: () => import('../views/public/CatalogView.vue'),
    meta: { public: true },
  },
  {
    path: '/carrito',
    name: 'public-cart',
    component: () => import('../views/public/CartView.vue'),
    meta: { public: true },
  },
  {
    path: '/nosotros',
    name: 'public-about',
    component: () => import('../views/public/AboutView.vue'),
    meta: { public: true },
  },
  {
    path: '/preguntas-frecuentes',
    name: 'public-faq',
    component: () => import('../views/public/FAQView.vue'),
    meta: { public: true },
  },
  {
    path: '/requisitos',
    name: 'public-requirements',
    component: () => import('../views/public/RequirementsView.vue'),
    meta: { public: true },
  },
  // External redirects and aliases (CLI-005)
  {
    path: '/solicitud',
    redirect: (to: any) => ({ path: '/panel/solicitar', query: to.query }),
  },
  {
    path: '/registro',
    redirect: (to: any) => ({ path: '/register', query: to.query }),
  },
  {
    path: '/perfil',
    redirect: (to: any) => ({ path: '/panel/perfil', query: to.query }),
  },
  {
    path: '/financiamientos',
    redirect: (to: any) => ({ path: '/panel/financiamientos', query: to.query }),
  },
  {
    path: '/cuotas',
    redirect: (to: any) => ({ path: '/panel/cuotas', query: to.query }),
  },
  {
    path: '/panel',
    component: () => import('../layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'dashboard', component: () => import('../views/DashboardView.vue') },
      { path: 'solicitar', name: 'request', component: () => import('../views/RequestView.vue') },
      { path: 'financiamientos', name: 'financings', component: () => import('../views/FinancingsView.vue') },
      { path: 'cuotas', name: 'payments', component: () => import('../views/PaymentsView.vue') },
      { path: 'reportar-pago', name: 'report-payment', component: () => import('../views/ReportPaymentView.vue') },
      { path: 'perfil', name: 'profile', component: () => import('../views/ProfileView.vue') },
      { path: 'notificaciones', name: 'notifications', component: () => import('../views/NotificationsView.vue') },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { title: 'Página no encontrada' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 }
  },
})

const guestOnlyRoutes = new Set(['login', 'register', 'forgot-password', 'reset-password'])

export function attachAuthNavigationGuard(targetRouter: typeof router) {
  targetRouter.beforeEach(async (to) => {
    const auth = useAuthStore()
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    const isGuestOnly = guestOnlyRoutes.has(to.name as string)

    if (!auth.initialized && !isGuestOnly) {
      await auth.initialize()
    }

    // If entering a protected route and user profile not loaded yet, try to fetch it
    if (requiresAuth && !auth.isAuthenticated) {
      await auth.fetchUser()
      if (!auth.isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } }
      }
    }

    // Authenticated user trying to access a guest-only route (e.g. login page)
    if (auth.isAuthenticated && isGuestOnly) {
      return { name: 'dashboard' }
    }
  })

  targetRouter.afterEach((to) => {
    if (typeof document !== 'undefined') {
      document.title = to.meta.title
        ? `${String(to.meta.title)} - BERA Services`
        : 'BERA Services - Portal de Clientes'
    }
  })
}

attachAuthNavigationGuard(router)

export default router
